import type { FluidStepInputs } from './fluidSim.js';

export const ENERGY_PARTICLE_LIMIT = 96;
const MAX_STEP = 0.05;
const TAU = Math.PI * 2;

interface EnergyParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  angle: number;
  age: number;
  life: number;
  seed: number;
}

/** Bounded gesture trails: petals for Bloom, spray for Sunset. */
export class EnergyParticles {
  private particles: EnergyParticle[] = [];
  private seed = 431;
  private emission = 0;
  private ambient = 0;
  private wasDown = false;
  private vertices = new Float32Array(ENERGY_PARTICLE_LIMIT * 8);
  energy = 0;
  holdSeconds = 0;
  point = { x: 0.5, y: 0.5 };

  constructor(private readonly bloom: boolean) {}
  get count(): number {
    return this.particles.length;
  }

  private random(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 0x100000000;
  }

  private emit(x: number, y: number, count: number, burst = false, dx = 0, dy = 0): void {
    for (let i = 0; i < count; i++) {
      const seed = this.random(),
        angle = this.random() * TAU;
      const speed = (burst ? 0.12 : 0.025) + this.random() * (burst ? 0.18 : 0.08);
      const particle: EnergyParticle = {
        x,
        y,
        vx: Math.cos(angle) * speed + dx * 0.28,
        vy: Math.sin(angle) * speed + (this.bloom ? 0.06 : 0.08) + dy * 0.28,
        size: this.bloom ? 0.028 + this.random() * 0.045 : 0.009 + this.random() * 0.012,
        angle,
        age: 0,
        life: this.bloom ? 2.2 + this.random() * 2.3 : 0.6 + this.random(),
        seed,
      };
      if (this.particles.length === ENERGY_PARTICLE_LIMIT) this.particles.shift();
      this.particles.push(particle);
    }
  }

  step(dt: number, inputs: FluidStepInputs): void {
    const delta = Math.max(0, Math.min(MAX_STEP, Number.isFinite(dt) ? dt : 0));
    const p = inputs.pointer;
    const x = Math.max(0, Math.min(1, Number.isFinite(p.x) ? p.x : 0.5));
    const y = Math.max(0, Math.min(1, Number.isFinite(p.y) ? p.y : 0.5));
    const dx = Math.max(
      -1.4,
      Math.min(1.4, (Number.isFinite(p.dx) ? p.dx : 0) / Math.max(delta, 0.016))
    );
    const dy = Math.max(
      -1.4,
      Math.min(1.4, (Number.isFinite(p.dy) ? p.dy : 0) / Math.max(delta, 0.016))
    );
    const speed = Math.hypot(dx, dy);
    for (const particle of this.particles) {
      particle.age += delta;
      if (this.bloom) {
        const distance = Math.hypot(particle.x - x, particle.y - y);
        const attraction = p.down ? Math.exp(-distance * 8) : 0;
        particle.vx +=
          (Math.sin(inputs.timeSec * 1.2 + particle.seed * TAU) * 0.04 + dx * attraction * 0.7) *
          delta;
        particle.vy += (0.028 + dy * attraction * 0.7) * delta;
        particle.angle += delta * (0.7 + particle.seed);
      } else {
        particle.vy -= 0.32 * delta;
      }
      particle.vx *= Math.exp(-delta * 0.5);
      particle.x += particle.vx * delta;
      particle.y += particle.vy * delta;
    }
    this.particles = this.particles.filter(
      (particle) =>
        particle.age < particle.life &&
        particle.x > -0.2 &&
        particle.x < 1.2 &&
        particle.y > -0.2 &&
        particle.y < 1.2
    );
    if (p.down) {
      this.point = { x, y: this.bloom ? y : Math.min(0.47, y) };
      this.holdSeconds = this.wasDown ? Math.min(8, this.holdSeconds + delta) : delta;
      this.energy = Math.min(1, this.energy + delta * 5);
      if (!this.wasDown) this.emit(this.point.x, this.point.y, this.bloom ? 9 : 10, true);
      this.emission += delta * (this.bloom ? 14 + speed * 26 : speed * 28);
      const count = Math.min(12, Math.floor(this.emission));
      if (count > 0) {
        this.emit(this.point.x, this.point.y, count, false, dx, dy);
        this.emission -= count;
      }
    } else {
      if (this.wasDown) {
        this.emit(this.point.x, this.point.y, this.bloom ? 28 : 8, true, dx, dy);
        this.emission = 0;
      }
      this.energy *= Math.exp(-delta * 1.1);
    }
    this.wasDown = p.down;
    if (this.bloom && inputs.active) {
      this.ambient += delta * (3 + inputs.intensity * 4);
      const count = Math.min(4, Math.floor(this.ambient));
      if (count > 0) {
        const a = inputs.timeSec * 0.7;
        this.emit(0.5 + Math.cos(a) * 0.12, 0.56 + Math.sin(a) * 0.12, count);
        this.ambient -= count;
      }
    }
  }

  vertexData(): Float32Array {
    let offset = 0;
    for (const p of this.particles) {
      this.vertices.set(
        [p.x, p.y, p.size, p.angle, p.age / p.life, this.bloom ? 1 : 0, p.seed, 1],
        offset
      );
      offset += 8;
    }
    return this.vertices.subarray(0, this.particles.length * 8);
  }
}
